import { translator } from "../../translation/translator";
import { notifier } from "../../core/notifier/notifier";
import { formValidator } from "../../util/form-validator";
import { UserSocialHandleCheckApi, type SocialHandleCheckResponse } from "./user-social-handle-check-api";
import { userSocialHandleCheckForm } from "./user-social-handle-check-form";
import { UserSocialHandleCheckListener } from "./user-social-handle-check-listener";

type CheckState = "idle" | "checking" | "valid" | "invalid";

const ICON_CLASSES = ["bi-arrow-repeat", "bi-check-circle-fill", "bi-x-circle-fill"];
const STATE_CLASSES = ["is-checking", "is-valid", "is-invalid"];

export class UserSocialHandleCheckSetup {
    public static init(el: Element): void {
        if (!(el instanceof HTMLElement)) return;
        if (el.dataset.socialHandleCheckBound === "1") return;
        el.dataset.socialHandleCheckBound = "1";

        const field = el.dataset.field || "";
        if (!field) return;

        const row = userSocialHandleCheckForm.getRow(el);
        const input = row ? userSocialHandleCheckForm.getFieldInput(row, field) : null;
        if (!(input instanceof HTMLInputElement)) return;

        UserSocialHandleCheckListener.bindBlur(input, () => {
            void this.runCheck(input, el, field);
        });

        const form = userSocialHandleCheckForm.getForm(input);
        if (form instanceof HTMLFormElement && form.dataset.socialHandleGuardBound !== "1") {
            form.dataset.socialHandleGuardBound = "1";
            UserSocialHandleCheckListener.bindSubmitGuard(form, (event) => {
                this.guardSubmit(form, event);
            });
        }
    }

    private static async runCheck(input: HTMLInputElement, statusEl: HTMLElement, field: string): Promise<void> {
        const value = String(input.value || "").trim();
        if (!value) {
            this.render(input, statusEl, "idle");
            return;
        }

        const platform = field.replace(/^social_/, "");
        this.render(input, statusEl, "checking");

        try {
            const response = await UserSocialHandleCheckApi.check(platform, value);
            if (!response.ok) {
                this.render(input, statusEl, "invalid");
                return;
            }

            const data = (await response.json().catch(() => ({}))) as SocialHandleCheckResponse;
            this.render(input, statusEl, data?.data?.exists === true ? "valid" : "invalid");
        } catch (error) {
            console.error("Social handle check failed:", error);
            this.render(input, statusEl, "invalid");
        }
    }

    private static render(input: HTMLInputElement, statusEl: HTMLElement, state: CheckState): void {
        statusEl.classList.remove(...ICON_CLASSES, ...STATE_CLASSES);

        if (state === "idle") {
            statusEl.removeAttribute("title");
            formValidator.removeFieldError(input);
            return;
        }

        if (state === "checking") {
            statusEl.classList.add("is-checking", "bi-arrow-repeat");
            statusEl.setAttribute("title", translator.getTranslation("social_handle_checking"));
            formValidator.removeFieldError(input);
            return;
        }

        if (state === "valid") {
            statusEl.classList.add("is-valid", "bi-check-circle-fill");
            statusEl.setAttribute("title", translator.getTranslation("social_handle_check_valid"));
            formValidator.removeFieldError(input);
            return;
        }

        statusEl.classList.add("is-invalid", "bi-x-circle-fill");
        statusEl.setAttribute("title", translator.getTranslation("social_handle_check_invalid"));
        formValidator.addFieldError(input, { focus: false });
    }

    private static guardSubmit(form: HTMLFormElement, event: SubmitEvent): void {
        const statusIcons = userSocialHandleCheckForm.getAllStatusIcons(form);

        for (const statusEl of statusIcons) {
            const field = statusEl.dataset.field || "";
            if (!field) continue;

            const row = userSocialHandleCheckForm.getRow(statusEl);
            const input = row ? userSocialHandleCheckForm.getFieldInput(row, field) : null;
            if (!(input instanceof HTMLInputElement)) continue;

            if (formValidator.hasFieldError(input)) {
                event.preventDefault();
                event.stopImmediatePropagation();
                notifier.error("social_handle_save_blocked", { duration: 5000 });
                formValidator.focusField(input);
                return;
            }
        }
    }
}
